import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import enCommon from "./locales/en/common.json";
import enFeatures from "./locales/en/features.json";
import enNavigation from "./locales/en/navigation.json";
import enValidation from "./locales/en/validation.json";
import enDashboard from "./locales/en/dashboard.json";
import enReports from "./locales/en/reports.json";
import deCommon from "./locales/de/common.json";
import deNavigation from "./locales/de/navigation.json";
import deValidation from "./locales/de/validation.json";
import deDashboard from "./locales/de/dashboard.json";
import deReports from "./locales/de/reports.json";
import esCommon from "./locales/es/common.json";
import esNavigation from "./locales/es/navigation.json";
import esValidation from "./locales/es/validation.json";
import esDashboard from "./locales/es/dashboard.json";
import esReports from "./locales/es/reports.json";
import frCommon from "./locales/fr/common.json";
import frNavigation from "./locales/fr/navigation.json";
import frValidation from "./locales/fr/validation.json";
import frDashboard from "./locales/fr/dashboard.json";
import frReports from "./locales/fr/reports.json";
import ptCommon from "./locales/pt/common.json";
import ptNavigation from "./locales/pt/navigation.json";
import ptValidation from "./locales/pt/validation.json";
import ptDashboard from "./locales/pt/dashboard.json";
import ptReports from "./locales/pt/reports.json";
import logger from "@shared/utils/logger";

// Sprint 2: Namespace-based i18n configuration with lazy loading
// REG-006: i18n Configuration for EU market access
// Supports: English (EN), German (DE), Spanish (ES), French (FR), Portuguese (PT)
// CE Mark requirement: IFU must be in local language

// Define all available namespaces
export const NAMESPACES = [
  "common", // Core UI elements (buttons, labels, actions) - EAGER
  "validation", // Form validation messages - EAGER
  "navigation", // Menu, breadcrumbs, app navigation - EAGER
  "auth", // Login, register, MFA - LAZY
  "patients", // Patient management - LAZY
  "studies", // Study management, EEG sessions - LAZY
  "reports", // Report generation - LAZY
  "dashboard", // Dashboard KPIs, widgets - LAZY
  "settings", // All settings (user, security, preferences) - LAZY
  "admin", // Admin panel - LAZY
  "errors", // Error messages - LAZY
  "features", // Feature-specific translations - LAZY
  "legal", // Legal, documentation, and compliance pages - LAZY
];

const isTestEnv = process.env.NODE_ENV === "test";
// Namespaces to preload on app mount (eager loading)
const EAGER_NAMESPACES = isTestEnv
  ? ["common", "navigation", "validation", "features", "dashboard", "reports"]
  : ["common", "navigation", "validation"];

// Tests use the same namespace structure as runtime without HTTP loading.
const TEST_TRANSLATION_RESOURCES = {
  en: {
    common: enCommon,
    features: enFeatures,
    navigation: enNavigation,
    validation: enValidation,
    dashboard: enDashboard,
    reports: enReports,
  },
  de: {
    common: deCommon,
    navigation: deNavigation,
    validation: deValidation,
    dashboard: deDashboard,
    reports: deReports,
  },
  es: {
    common: esCommon,
    navigation: esNavigation,
    validation: esValidation,
    dashboard: esDashboard,
    reports: esReports,
  },
  fr: {
    common: frCommon,
    navigation: frNavigation,
    validation: frValidation,
    dashboard: frDashboard,
    reports: frReports,
  },
  pt: {
    common: ptCommon,
    navigation: ptNavigation,
    validation: ptValidation,
    dashboard: ptDashboard,
    reports: ptReports,
  },
};

let i18nBuilder = i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next);

if (!isTestEnv) {
  // HTTP Backend for namespace loading (disabled in tests)
  i18nBuilder = i18nBuilder.use(Backend);
}

i18nBuilder
  // Initialize i18next with namespace configuration
  .init({
    // Avoid i18next's promotional support notice in application and test logs.
    showSupportNotice: false,
    // Only load eager namespaces on init (others lazy loaded on demand)
    ns: EAGER_NAMESPACES,

    // Default namespace (always loaded)
    defaultNS: "common",
    fallbackNS: false,

    // Fallback language
    fallbackLng: "en",
    lng: isTestEnv ? "en" : undefined,

    // Debug mode in development
    debug: process.env.NODE_ENV === "development",

    // Language detection order — querystring first so mobile handoffs (?lang=)
    // render the requested portal language before localStorage/navigator.
    detection: {
      order: ["querystring", "localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
      lookupQuerystring: "lang",
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Backend configuration for namespace loading
    backend: {
      // Load path: /locales/{language}/{namespace}.json
      loadPath: "/locales/{{lng}}/{{ns}}.json",

      // Request timeout
      requestOptions: {
        cache: "default",
      },
    },

    // React-specific options
    react: {
      // Use Suspense for loading states
      useSuspense: true,

      // Bind i18n instance to React
      bindI18n: "languageChanged loaded",
      bindI18nStore: "added removed",
    },

    // Load namespaces on demand when accessed
    load: "languageOnly", // Only load 'en' instead of 'en-US'

    // Namespace separator (namespace:key)
    nsSeparator: ":",

    // Key separator (nested.key.path)
    keySeparator: ".",

    // Sprint 3: Fix TypeScript - prevent t() from returning null
    returnNull: false,

    // Provide static resources in test environment for deterministic lookups.
    resources: isTestEnv ? TEST_TRANSLATION_RESOURCES : undefined,
    partialBundledLanguages: isTestEnv,
  });

// Update HTML lang attribute when language changes
i18n.on("languageChanged", (lng: string) => {
  document.documentElement.lang = lng;

  // Log namespace loading in development
  if (process.env.NODE_ENV === "development") {
    logger.info(`[i18n] Language changed to: ${lng}`);
  }
});

/** Mobile / deep-link continuity: honour lang|lng query even if detector missed it. */
function applyQueryLanguageFromLocation(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("lang") || params.get("lng");
    if (!raw) return;
    const code = raw.trim().toLowerCase().split(/[-_]/)[0] || "en";
    if (!["en", "de", "es", "fr", "pt"].includes(code)) return;
    window.localStorage.setItem("i18nextLng", code);
    if (i18n.language !== code) {
      void i18n.changeLanguage(code);
    }
    document.documentElement.lang = code;
  } catch {
    // Ignore malformed URLs; detector fallbacks still apply.
  }
}

applyQueryLanguageFromLocation();
if (typeof window !== "undefined") {
  window.addEventListener("popstate", applyQueryLanguageFromLocation);
}

// Log lazy-loaded namespaces in development
if (process.env.NODE_ENV === "development") {
  i18n.on("loaded", (loaded: any) => {
    const namespaces = Object.keys(loaded[i18n.language] || {});
    if (namespaces.length > 0) {
      logger.info(`[i18n] Loaded namespaces for ${i18n.language}:`, namespaces);
    }
  });
}

// Export types for external use
export type { Namespace, NamespaceKeys } from "./types";

if (typeof window !== "undefined") {
  (
    window as Window & {
      __i18n?: typeof i18n;
    }
  ).__i18n = i18n;
}

export default i18n;
